import { ExecutionResult } from '@modules/test-development/models/execution-result.model';
import { Page } from '@testsigma/organism';
import api from '../api/instance';
import { useInfiniteQuery, useQuery } from 'react-query';
import { PrivateURLConstants } from '@constants/private-url.constant';
import { EntityType } from '@modules/test-development/enums/entity-type.enum';
import { ReRunHistory } from '@modules/test-development/models/re-run-history.model';

const nameSpace = PrivateURLConstants.EXECUTION_RESULTS;

const create = (payload: ExecutionResult) => {
  return api
    .post<ExecutionResult>(nameSpace, payload.serialize())
    .then((res) => new ExecutionResult().deserialize(res))
    .catch((e) => {
      throw e?.response?.data?.error;
    });
};

const update = (payload: ExecutionResult) => {
  return api
    .put<ExecutionResult>(nameSpace + `/${payload.id}`, payload)
    .then((res) => new ExecutionResult().deserialize(res))
    .catch((e) => Promise.reject(e));
};

const getRunningCounts = () => {
  // Removed api call for now, Need to remove the dependencies
  return new Promise((resolve) => {
    const page = new Page<ExecutionResult>();
    page.content = [];
    resolve(page);
  });
};

const findAll = (query?, size?, page?, sort?) => {
  return api
    .get(nameSpace, {
      query,
      size,
      page,
      sort,
    })
    .then((res) =>
      new Page<ExecutionResult>().deserialize(res, ExecutionResult),
    )
    .catch((e) => Promise.reject(e));
};

const show = (id: number) => {
  return api
    .get(`${nameSpace}/${id}`)
    .then((res) => new ExecutionResult().deserialize(res))
    .catch((e) => Promise.reject(e));
};

const getParentResult = (id: number) => {
  return api
    .get(`${nameSpace}/parent_result/${id}`)
    .then((res) => new ExecutionResult().deserialize(res))
    .catch((e) => Promise.reject(e));
};

const stop = (executionResultId: number) => {
  return api
    .get(`${nameSpace}/stop/${executionResultId}`)
    .then((res) => res)
    .catch((err) => Promise.reject(err));
};

const destroy = (executionResultId: number) => {
  return api.remove(`${nameSpace}/${executionResultId}`);
};

const findFirstParentExecutionResult = (childResultId: number) => {
  return new Promise((resolve, reject) => {
    api
      .get<ExecutionResult>(
        nameSpace + `/get_first_parent_result/${childResultId}`,
      )
      .then((res) => resolve(new ExecutionResult().deserialize(res)))
      .catch((e) => reject(e));
  });
};

const generateXLSReport = (executionId: number, runId: number) => {
  return api
    .get(`${nameSpace}/generate_xls/${executionId}/runs/${runId}`)
    .then((res) => res)
    .catch((err) => Promise.reject(err));
};

const find = (runId: number) => {
  return api
    .get(`${nameSpace}/${runId}`)
    .then((res) => new ExecutionResult().deserialize(res))
    .catch((err) => Promise.reject(err));
};

const getEntityCount = (entityType: EntityType, runId: number) => {
  return api
    .get(`${nameSpace}/counts/${runId}?entityType=${entityType}`)
    .then((res) => res)
    .catch((err) => Promise.reject(err));
};

export const useFetchEntityCount = ({
  enabled,
  runId,
  entityType,
  refetchOnMount,
  cacheKey = [],
}) => {
  return useQuery({
    queryKey: [runId, entityType, ...cacheKey].filter(Boolean),
    queryFn: async () => {
      const response = await getEntityCount(entityType, runId);
      return response;
    },
    enabled,
    refetchOnMount: refetchOnMount ?? true,
  });
};

export const useFetchExecutionResult = ({ enabled, runId, callback }) => {
  return useQuery({
    queryKey: `execution-result-${runId}`,
    queryFn: async () => {
      const response: ExecutionResult = await find(runId);
      if (callback) {
        callback();
      }
      return response;
    },
    enabled,
  });
};

export const useFetchAllExecutionResults = ({
  query,
  enabled,
  size,
  sort = '',
  queryCacheKey = [],
}) => {
  return useInfiniteQuery(
    [`execution-results-${query}`, ...queryCacheKey],
    async ({ pageParam = 0 }) => {
      const response: Page<ExecutionResult> = await findAll(
        query,
        size,
        pageParam,
        sort,
      );
      return new Page<ExecutionResult>().deserialize(response, ExecutionResult);
    },
    {
      enabled,
      getNextPageParam: (lastPage) =>
        lastPage?.last ? undefined : lastPage?.pageable?.pageNumber + 1,
      cacheTime: 0,
      staleTime: 0,
    },
  );
};

const reRunHistoryList = (runId: number) => {
  return api
    .get<ReRunHistory[]>(`${nameSpace}/run_history/${runId}`)
    .then((res) => res)
    .catch((err) => Promise.reject(err));
};

const updateExecutionResult = ({ runId, buildNo }) => {
  return api
    .patch(`${nameSpace}/update-build/${runId}`, { buildNo })
    .then((res) => res)
    .catch((err) => Promise.reject(err));
};

const comparisonResult = (executionResultId) =>
  api.get(`${nameSpace}/penultimate_result/${executionResultId}`);

export const ExecutionResultService = {
  create,
  update,
  getRunningCounts,
  findAll,
  show,
  stop,
  destroy,
  findFirstParentExecutionResult,
  getParentResult,
  generateXLSReport,
  find,
  getEntityCount,
  reRunHistoryList,
  updateExecutionResult,
  comparisonResult,
};
